import i18n from '../i18n'

/**
 * The first-run flow's rules (design page 1, `onboarding`; P-onboarding of
 * the go-live plan; the two questions of V3-w), mirrored from the Android
 * app's `OnboardingScreen.kt` and its `onboarding_done` flag: three slides,
 * seen once, skipped or finished alike — and then two questions, the level
 * and the minutes a day, kept on the device until there is a session to
 * send them with (`onboarding_complete`, V3-b).
 */

const t = (key, options) => i18n.t(key, { ns: 'onboarding', ...options })

/**
 * The three slides, read through i18n at access time (the same getter
 * trick as `LEVEL_LABELS`) so a language switch re-reads them and the
 * page's `STEPS.map(...)` keeps working.
 */
const SLIDE_KEYS = ['shadowing', 'analysis', 'daily']
export const STEPS = SLIDE_KEYS.map((key) => ({
  key,
  get title() {
    return t(`slides.${key}.title`)
  },
  get body() {
    return t(`slides.${key}.body`)
  },
}))

export const ONBOARDING_KEY = 'shadowtooti.onboardingDone'
/** The two answers, waiting for a session to send them (or for the network to come back). */
export const ANSWERS_KEY = 'shadowtooti.onboardingAnswers'
/** Set once the two questions have been put to this device and answered or dismissed. */
export const ASKED_KEY = 'shadowtooti.onboardingAsked'

export function hasSeenOnboarding() {
  return readFlag(ONBOARDING_KEY)
}

export function markOnboardingSeen() {
  writeFlag(ONBOARDING_KEY)
}

/**
 * Where the app starts, the design's rule: the lessons if there is a
 * session, otherwise sign-in if the carousel has been seen, otherwise the
 * carousel. A learner with a session never sees it.
 */
export function startDestination(status, seen) {
  if (status === 'authenticated') return '/dashboard'
  return seen ? '/auth' : '/onboarding'
}

/** The button's label: «بعدی» on the way, «شروع» on the last step. */
export function ctaLabel(step) {
  return step >= STEPS.length - 1 ? t('start') : t('next')
}

// ---------------------------------------------------------------------------
// The two questions (V3-w): a pure reducer the page drives.
// ---------------------------------------------------------------------------

export const LEVELS = ['beginner', 'intermediate', 'advanced']

/** The server's default `onboarding.minuteOptions`, until `onboarding_samples` answers. */
export const DEFAULT_MINUTE_OPTIONS = [5, 10, 15, 20]

/**
 * The steps, in the order the page walks them:
 *
 *   level ──level/picked──▶ minutes ──minutes/picked──▶ done
 *   level ──level/unknown──▶ listen ──level/picked──▶ minutes
 *   listen ──back──▶ level;  minutes ──back──▶ level | listen (wherever it came from)
 *
 * `samples/loaded` and `samples/failed` are accepted in any step: the
 * samples are fetched as the page opens and may land while the learner is
 * still on the first question.
 */
export const QUESTION_STEPS = ['level', 'listen', 'minutes', 'done']

export function initialQuestions() {
  return {
    step: 'level',
    level: null,
    dailyMinutes: null,
    pickedFromListen: false,
    /** `[{ level, samples: [{ position, audioUrl, textEn, durationMs }] }]`, or null until loaded. */
    levels: null,
    minuteOptions: DEFAULT_MINUTE_OPTIONS,
    samplesFailed: false,
  }
}

function validMinuteOptions(options) {
  return Array.isArray(options) && options.length > 0 && options.every((n) => Number.isInteger(n) && n > 0)
}

/** The three levels in the fixed order, whatever the server's list looked like; a missing level has no samples. */
function normaliseLevels(levels) {
  const byLevel = new Map((Array.isArray(levels) ? levels : []).map((entry) => [entry?.level, entry]))
  return LEVELS.map((level) => {
    const samples = byLevel.get(level)?.samples
    return {
      level,
      samples: (Array.isArray(samples) ? samples : []).filter((sample) => typeof sample?.audioUrl === 'string').slice(0, 3),
    }
  })
}

/** A transition the current step does not allow leaves the state untouched (same object). */
export function questionsReducer(state, action) {
  switch (action.type) {
    case 'samples/loaded':
      return {
        ...state,
        levels: normaliseLevels(action.samples?.levels),
        minuteOptions: validMinuteOptions(action.samples?.minuteOptions) ? action.samples.minuteOptions : state.minuteOptions,
        samplesFailed: false,
      }
    case 'samples/failed':
      return state.levels ? state : { ...state, samplesFailed: true }
    case 'level/picked': {
      if (state.step !== 'level' && state.step !== 'listen') return state
      if (!LEVELS.includes(action.level)) return state
      return { ...state, level: action.level, pickedFromListen: state.step === 'listen', step: 'minutes' }
    }
    case 'level/unknown':
      return state.step === 'level' ? { ...state, step: 'listen' } : state
    case 'minutes/picked': {
      if (state.step !== 'minutes') return state
      if (!state.minuteOptions.includes(action.minutes)) return state
      return { ...state, dailyMinutes: action.minutes, step: 'done' }
    }
    case 'back': {
      if (state.step === 'listen') return { ...state, step: 'level' }
      if (state.step === 'minutes') return { ...state, step: state.pickedFromListen ? 'listen' : 'level' }
      return state
    }
    default:
      return state
  }
}

/** The two answers, once both are given; null before that. */
export function answersOf(state) {
  if (state.step !== 'done') return null
  return { level: state.level, dailyMinutes: state.dailyMinutes }
}

/** The samples of one level, in position order; empty until loaded or when the admin has filled no slot. */
export function samplesFor(state, level) {
  const samples = state.levels?.find((entry) => entry.level === level)?.samples ?? []
  return [...samples].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
}

/** Which question the step is on, for «سؤال ۱ از ۲»: the listening screen is still question one. */
export function questionNumber(step) {
  return step === 'minutes' || step === 'done' ? 2 : 1
}

// ---------------------------------------------------------------------------
// Keeping the answers on the device, and deciding when to ask (Q3).
// ---------------------------------------------------------------------------

function isAnswers(value) {
  return Boolean(value) && LEVELS.includes(value.level) && Number.isInteger(value.dailyMinutes) && value.dailyMinutes > 0
}

/** The answers waiting to be sent, or null. */
export function readPendingAnswers() {
  try {
    const stored = JSON.parse(localStorage.getItem(ANSWERS_KEY) ?? 'null')
    return isAnswers(stored) ? { level: stored.level, dailyMinutes: stored.dailyMinutes } : null
  } catch {
    return null
  }
}

export function savePendingAnswers(answers) {
  if (!isAnswers(answers)) return
  try {
    localStorage.setItem(ANSWERS_KEY, JSON.stringify({ level: answers.level, dailyMinutes: answers.dailyMinutes }))
  } catch {
    // No storage: the answers are lost with the page; the questions come back after sign-in (Q3).
  }
}

export function clearPendingAnswers() {
  try {
    localStorage.removeItem(ANSWERS_KEY)
  } catch {
    // Nothing to clear.
  }
}

export function hasBeenAsked() {
  return readFlag(ASKED_KEY)
}

export function markAsked() {
  writeFlag(ASKED_KEY)
}

/**
 * Whether a signed-in learner should be put the two questions now: never
 * once the server has the answers (`onboardedAt`), never while answers are
 * waiting to be sent, and otherwise once per device — an existing learner
 * sees them after the update and may dismiss them (decision Q3).
 */
export function shouldAskQuestions(user, { pending = null, asked = false } = {}) {
  if (!user) return false
  if (user.onboardedAt) return false
  if (pending) return false
  return !asked
}

/** The statuses that mean the server looked at the answers and refused them — no point sending the same again. */
const REFUSED = new Set([400, 403, 404, 422])

/**
 * After a failed `onboarding_complete`: keep the answers for the next app
 * open (the network, a 5xx, an expired session) or drop them (the server
 * refused them — `invalid_daily_minutes` when the panel changed the
 * options in between; the questions will come back after sign-in).
 */
export function keepAnswersAfter(error) {
  const status = error?.response?.status
  return !REFUSED.has(status)
}

/** The profile as `onboarding_complete` leaves it: the same three fields it returns. */
export function applyOnboardingState(user, state) {
  return { ...user, level: state.level, dailyMinutes: state.dailyMinutes, onboardedAt: state.onboardedAt }
}

function readFlag(key) {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeFlag(key) {
  try {
    localStorage.setItem(key, '1')
  } catch {
    // No storage: the screen may show again next time; better than blocking sign-in.
  }
}
